import { useEffect } from "react";
import { Button } from "../Button";
import { useToastAvoid } from "../Toast";
import { useNextT } from "../../i18n";
import s from "./SaveBar.module.scss";

type Props = {
  /** Detail after "Unsaved changes" ("3 changes", "Dovid will see 132 contacts"). */
  count?: string;
  onDiscard: () => void;
  onSave: () => void;
  secondary?: { label: string; onClick: () => void };
  /** The Save button's words when "Save" alone is not enough (e.g. "Save for every account"). */
  saveLabel?: string;
  /** Saving: Save shows it is working and neither button can be pressed. */
  busy?: boolean;
  /** Neither Save button can be pressed (e.g. the page is waiting on something); `hint` says why, in the bar. */
  disabled?: boolean;
  hint?: string;
  /** On phones the bar takes the place of the page's bottom action (a fixed bar), instead of sitting above it. */
  phoneReplacesBottomBar?: boolean;
};

/**
 * Boards 86/87: the sticky bar at the bottom of a page with unsaved changes. A labelled region (it is not a live
 * region and never takes focus when it appears). Tab order: Discard, the secondary action, Save (the one primary).
 * Toasts stay above it.
 */
export const SaveBar = ({ count, onDiscard, onSave, secondary, saveLabel, busy, disabled, hint, phoneReplacesBottomBar }: Props) => {
  const { t } = useNextT();
  const avoid = useToastAvoid();
  // A flag on the frame's content for the browsers without `:has()` (NextFrame.module.scss keeps the room for the fixed bar).
  useEffect(() => {
    if (!phoneReplacesBottomBar) return;
    const main = document.getElementById("cy-main");
    if (!main) return;
    const n = Number(main.dataset.phoneBars ?? 0) + 1;
    main.dataset.phoneBars = String(n);
    return () => {
      const left = Number(main.dataset.phoneBars ?? 1) - 1;
      if (left > 0) main.dataset.phoneBars = String(left);
      else delete main.dataset.phoneBars;
    };
  }, [phoneReplacesBottomBar]);
  return (
    <div ref={avoid} role="region" aria-label={t("ui.unsaved_changes")} className={s.bar} data-phone-replaces={phoneReplacesBottomBar ? "true" : undefined}>
      <p className={s.text}>
        <strong>{t("ui.unsaved_changes")}</strong>
        {count && <span className={s.detail}> · {count}</span>}
        {hint && <span className={s.detail}> · {hint}</span>}
      </p>
      <div className={s.actions}>
        <Button variant="ghost" disabled={busy} onClick={onDiscard}>{t("ui.discard")}</Button>
        {secondary && <Button variant="secondary" disabled={busy || disabled} onClick={secondary.onClick}>{secondary.label}</Button>}
        <Button variant="primary" loading={busy} disabled={disabled} onClick={onSave}>{saveLabel ?? t("ui.save")}</Button>
      </div>
    </div>
  );
};
